import { DASHBOARD_CHART_TYPES } from '@control-front-end/common/constants/graphActors';
import {
  TREND_LINE_BORDER_DASH,
  TREND_LINE_POINT_RADIUS,
  TREND_LINE_LABEL_SUFFIX,
} from '../constants';

const TREND_LINE_PROPS = {
  backgroundColor: 'transparent',
  borderDash: TREND_LINE_BORDER_DASH,
  pointRadius: TREND_LINE_POINT_RADIUS,
  type: DASHBOARD_CHART_TYPES.LINE,
  fill: false,
  tension: 0.3,
  hidden: false,
  isPluginData: true,
};

/**
 * Calculate least squares regression line
 * @param {Array} data - Array of data points with x and y properties
 * @returns {Object} - Regression line parameters { slope, intercept }
 */
const calculateRegressionLine = (data) => {
  if (!data || data.length < 2) {
    return { slope: 0, intercept: 0 };
  }

  const normalizedData = data.map((point, index) => ({
    x: index, // Normalize x to sequential index
    y: typeof point === 'object' ? point.y : point,
  }));

  const n = normalizedData.length;

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;

  for (let i = 0; i < n; i += 1) {
    const { x, y } = normalizedData[i];
    sumX += x;
    sumY += y;
    sumXY += x * y;
    sumXX += x * x;
  }

  const numerator = n * sumXY - sumX * sumY;
  const denominator = n * sumXX - sumX * sumX;

  const slope = denominator === 0 ? 0 : numerator / denominator;
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
};

/**
 * Generate trend line data points from regression parameters
 * @param {Array} originalData - Original data points
 * @param {Object} regression - Regression parameters { slope, intercept }
 * @returns {Array} - Trend line data points
 */
const generateTrendLineData = (originalData, regression) => {
  if (!originalData || originalData.length < 2) {
    return [];
  }

  const { slope, intercept } = regression;
  return originalData.map((_, index) => ({
    x: originalData[index].x,
    y: slope * index + intercept,
  }));
};

/**
 * Chart.js plugin to calculate and display trend lines
 */
const TrendLinePlugin = {
  id: 'trendLine',

  beforeInit(chart) {
    chart._trendlinePluginData = {
      updatedAt: Date.now(),
    };
  },

  afterInit(chart) {
    this.processTrendLines(chart);
  },

  afterUpdate(chart) {
    if (this.hasDataChanged(chart)) {
      this.processTrendLines(chart);
    }
  },

  /**
   * Check if the dataset has changed since the last calculation
   */
  hasDataChanged(chart) {
    if (!chart._trendlinePluginData) return true;

    const hasChanged = chart._updatedAt > chart._trendlinePluginData.updatedAt;

    if (hasChanged) {
      chart._trendlinePluginData.updatedAt = chart._updatedAt;
    }

    return hasChanged;
  },

  /**
   * Process and add trend lines for datasets with showTrendLine: true
   */
  processTrendLines(chart) {
    if (!chart._trendlinePluginData) return;

    const { data, options } = chart;
    const { scales } = options;

    if (!scales || !scales.x || !scales.y) return;

    const originalDatasets = data.datasets.filter((i) => !i.isPluginData);
    const trendLines = [];

    const isStackedBar =
      chart.config.type === DASHBOARD_CHART_TYPES.BAR &&
      options.scales &&
      options.scales.y &&
      options.scales.y.stacked;

    let showStackedTrendLine = false;
    if (isStackedBar) {
      showStackedTrendLine = chart.data.datasets.some(
        (dataset) => dataset.showTrendLine
      );
    }

    originalDatasets.forEach((dataset, index) => {
      if (!dataset.showTrendLine && !(isStackedBar && showStackedTrendLine)) {
        return;
      }

      if (!dataset.data || dataset.data.length < 2) {
        return;
      }

      if (isStackedBar && showStackedTrendLine) {
        if (index > 0) return;

        const summedData = [];
        for (let i = 0; i < dataset.data.length; i += 1) {
          let sum = 0;
          for (let j = 0; j < originalDatasets.length; j += 1) {
            const value = originalDatasets[j].data[i];
            sum += typeof value === 'object' ? value.y : value;
          }
          summedData.push({ x: i, y: sum });
        }

        const regression = calculateRegressionLine(summedData);
        const trendLineDataset = {
          label: `Total${TREND_LINE_LABEL_SUFFIX}`,
          data: generateTrendLineData(summedData, regression),
          borderColor: dataset.borderColor,
          originalDatasetIndex: -1, // doesn't belong to any original dataset
          ...TREND_LINE_PROPS,
        };
        trendLines.push(trendLineDataset);
      } else {
        const regression = calculateRegressionLine(dataset.data);
        const trendLineDataset = {
          label: dataset.label + TREND_LINE_LABEL_SUFFIX,
          data: generateTrendLineData(dataset.data, regression),
          borderColor: dataset.borderColor,
          originalDatasetIndex: index,
          ...TREND_LINE_PROPS,
        };
        trendLines.push(trendLineDataset);
      }
    });
    chart.data.datasets = [...originalDatasets, ...trendLines];
    chart.update();
  },
};

export default TrendLinePlugin;
